import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { useAuth } from '@/context/AuthContext';
import type {
  ApiResponse,
  IdentityBoard,
  BoardCompleteness,
  InterestsResponse,
  RefInterest,
} from '@/api/types';
import {
  Edit3,
  Loader2,
  GraduationCap,
  Plane,
  Heart,
  Coffee,
  CheckCircle,
  Eye,
  EyeOff,
  LogOut,
} from 'lucide-react';
import clsx from 'clsx';
import ProfileContributions from '@/components/ProfileContributions';

const BLOCK_CONFIG = [
  { key: 'core', icon: GraduationCap, title: 'Core', color: 'primary', dataKey: 'coreBlocks' },
  { key: 'journey', icon: Plane, title: 'Journey', color: 'accent', dataKey: 'journeyBlocks' },
  { key: 'interests', icon: Heart, title: 'Interests', color: 'twins', dataKey: 'interestsBlocks' },
  { key: 'lifestyle', icon: Coffee, title: 'Lifestyle', color: 'strong', dataKey: 'lifestyleBlocks' },
] as const;

export default function Profile() {
  const { user, logout } = useAuth();

  const { data: boardData, isLoading: boardLoading } = useQuery({
    queryKey: ['identity-board'],
    queryFn: () => api.get<ApiResponse<IdentityBoard>>('/identity-board'),
  });

  const { data: completenessData } = useQuery({
    queryKey: ['identity-board-completeness'],
    queryFn: () => api.get<ApiResponse<BoardCompleteness>>('/identity-board/completeness'),
    enabled: !!boardData,
  });

  // Interest values are stored as RefInterest codes (e.g., "cricket_modern").
  // Load the reference list once and resolve codes to display names. If a value
  // isn't a known code (legacy data predating the code switch, or a stale
  // code), fall back to showing the raw string so we never render a blank.
  const { data: interestsData } = useQuery({
    queryKey: ['interests'],
    queryFn: () => api.get<ApiResponse<InterestsResponse>>('/interests'),
  });
  const interestByCode = useMemo(() => {
    const map = new Map<string, RefInterest>();
    for (const i of interestsData?.data?.items ?? []) map.set(i.code, i);
    return map;
  }, [interestsData]);
  const displayInterest = (value: string): string => {
    const hit = interestByCode.get(value);
    return hit ? hit.canonicalNameEn : value;
  };

  const board = boardData?.data;
  const completeness = completenessData?.data;

  if (boardLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Profile Header */}
      <div className="card p-6 mb-6">
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary-400 to-accent-400 flex items-center justify-center text-white font-bold text-2xl flex-shrink-0">
            {user?.name?.charAt(0) || '?'}
          </div>

          <div className="flex-1">
            <h1 className="text-2xl font-display font-bold">{user?.name}</h1>
            <p className="text-gray-600">{user?.email}</p>
            <Link
              to="/profile/extended"
              className="text-sm text-primary-600 hover:underline inline-block mt-1"
            >
              extended profile & privacy →
            </Link>

            {board && (
              <div className="flex items-center gap-4 mt-3">
                <div className="text-sm">
                  <span className="text-gray-500">Completeness:</span>{' '}
                  <span className="font-semibold text-primary-600">
                    {completeness?.totalScore || 0}%
                  </span>
                </div>
                <div className="text-sm">
                  <span className="text-gray-500">Discoverable:</span>{' '}
                  {board.isDiscoverable ? (
                    <span className="font-semibold text-twins-dark flex items-center gap-1 inline-flex">
                      <Eye className="w-4 h-4" /> Yes
                    </span>
                  ) : (
                    <span className="font-semibold text-gray-500 flex items-center gap-1 inline-flex">
                      <EyeOff className="w-4 h-4" /> No
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          <Link to="/lab/identity-board/create" className="btn-outline flex items-center gap-2">
            <Edit3 className="w-4 h-4" />
            Edit Board
          </Link>
        </div>
      </div>

      {user && <ProfileContributions key={user.id} viewerId={user.id} />}

      {/* Board Summary */}
      {board && (
        <>
          {/* Blocks Overview */}
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {BLOCK_CONFIG.map((block) => {
              const Icon = block.icon;
              const blockData = completeness?.breakdown[block.key];
              const isComplete = blockData && blockData.filled.length > 0;

              const colorClasses: Record<string, { bg: string; text: string; light: string }> = {
                primary: { bg: 'bg-primary-100', text: 'text-primary-600', light: 'bg-primary-50' },
                accent: { bg: 'bg-accent-100', text: 'text-accent-600', light: 'bg-accent-50' },
                twins: { bg: 'bg-twins-light', text: 'text-twins-dark', light: 'bg-green-50' },
                strong: { bg: 'bg-strong-light', text: 'text-strong-dark', light: 'bg-blue-50' },
              };

              const colors = colorClasses[block.color];

              return (
                <div
                  key={block.key}
                  className={clsx('card p-4', isComplete && colors.light)}
                >
                  <div className="flex items-start gap-3">
                    <div className={clsx('w-10 h-10 rounded-lg flex items-center justify-center', colors.bg, colors.text)}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold">{block.title}</h3>
                        {isComplete && <CheckCircle className="w-4 h-4 text-twins" />}
                      </div>
                      {blockData ? (
                        <div className="text-sm text-gray-600">
                          {blockData.filled.length > 0 ? (
                            <span>{blockData.filled.length} fields filled</span>
                          ) : (
                            <span className="text-gray-400">Not started</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">Loading...</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Board Details */}
          <div className="card p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4">Your Board Details</h2>

            <div className="space-y-4">
              {/* Era */}
              {board.core?.eraDecade && (
                <div>
                  <label className="text-sm text-gray-500">Era</label>
                  <p className="font-medium">
                    {board.core.eraDecade.replace('_', ' ').replace(/^\w/, (c: string) => c.toUpperCase())}
                    {board.core.eraLabel && ` - "${board.core.eraLabel}"`}
                  </p>
                </div>
              )}

              {/* Migration */}
              {board.journey?.migrationNarrative && (
                <div>
                  <label className="text-sm text-gray-500">Migration Journey</label>
                  <p className="font-medium">
                    {board.journey.migrationNarrative
                      .replace(/_/g, ' ')
                      .replace(/^\w/, (c: string) => c.toUpperCase())}
                  </p>
                </div>
              )}

              {/* School & College */}
              {(board.core?.schoolType || board.core?.collegeType) && (
                <div>
                  <label className="text-sm text-gray-500">Education</label>
                  <p className="font-medium">
                    {[board.core.schoolType, board.core.collegeType]
                      .filter(Boolean)
                      .map((t) => t?.replace(/_/g, ' ').toUpperCase())
                      .join(', ')}
                  </p>
                </div>
              )}

              {/* Interests */}
              {board.interests?.sports && board.interests.sports.length > 0 && (
                <div>
                  <label className="text-sm text-gray-500">Sports</label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {board.interests.sports.map((sport: string) => (
                      <span key={sport} className="text-sm bg-gray-100 px-2 py-0.5 rounded">
                        {displayInterest(sport)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {board.interests?.hobbies && board.interests.hobbies.length > 0 && (
                <div>
                  <label className="text-sm text-gray-500">Hobbies</label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {board.interests.hobbies.map((hobby: string) => (
                      <span key={hobby} className="text-sm bg-gray-100 px-2 py-0.5 rounded">
                        {displayInterest(hobby)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Connection Intents */}
              {board.lifestyle?.connectionIntents && board.lifestyle.connectionIntents.length > 0 && (
                <div>
                  <label className="text-sm text-gray-500">Looking for</label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {board.lifestyle.connectionIntents.map((intent: string) => (
                      <span key={intent} className="text-sm bg-primary-50 text-primary-700 px-2 py-0.5 rounded">
                        {intent.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* No Board */}
      {!board && (
        <div className="card p-6 text-center mb-6">
          <h2 className="text-lg font-semibold mb-2">No Identity Board Yet</h2>
          <p className="text-gray-600 mb-4">
            Create your Identity Board to start finding your people.
          </p>
          <Link to="/lab/identity-board/create" className="btn-primary">
            Create Your Board
          </Link>
        </div>
      )}

      {/* Logout */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold mb-4">Account</h2>
        <button
          onClick={logout}
          className="btn-ghost text-red-600 hover:text-red-700 hover:bg-red-50 flex items-center gap-2"
        >
          <LogOut className="w-4 h-4" />
          Sign Out
        </button>
      </div>
    </div>
  );
}
